/**
 * PhotoScoring — AI-powered photo quality scoring for property images.
 * Shows scores per image, auto-orders by total score, suggests hero image.
 */
import { useState } from 'react';
import { Star, ArrowUpDown, Loader2, Trophy, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useAuthStore } from '@/stores/auth';
import { ai } from '@/lib/api';
import { cn } from '@/lib/utils';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.prima.myx.is';

// Icelandic category labels
const categoryLabels: Record<string, string> = {
  ytra_utlit: 'Ytra útlit',
  eldhus: 'Eldhús',
  stofa: 'Stofa/borðstofa',
  svalir: 'Svalir/verönd',
  badherbergi: 'Baðherbergi',
  svefnherbergi_adal: 'Aðal svefnherbergi',
  svefnherbergi: 'Svefnherbergi',
  annad: 'Annað',
  thvottahus: 'Þvottahús',
  geymslur: 'Geymsla/bílskúr',
};

// Score color bands
function getScoreColor(total: number): string {
  if (total >= 100) return 'text-emerald-600 bg-emerald-50';
  if (total >= 85) return 'text-blue-600 bg-blue-50';
  if (total >= 70) return 'text-amber-600 bg-amber-50';
  return 'text-gray-600 bg-gray-50';
}

interface ImageScore {
  category: string;
  category_en?: string;
  score: number;
  quality: number;
  total: number;
  confidence: number;
  suggested_order?: number;
  detected_objects?: string[];
}

interface PhotoScoringProps {
  propertyId: string;
  images: string[];
  imageScores?: Record<string, ImageScore>;
  onReorder?: (orderedImages: string[]) => void;
}

export function PhotoScoring({
  propertyId,
  images,
  imageScores = {},
  onReorder,
}: PhotoScoringProps) {
  const { token } = useAuthStore();
  const { toast } = useToast();
  const [scoring, setScoring] = useState(false);
  const [scores, setScores] = useState<Record<string, ImageScore>>(imageScores);

  const hasScores = Object.keys(scores).length > 0;
  const scoredCount = images.filter(img => scores[img]).length;

  const handleScoreAll = async () => {
    if (!token) return;
    setScoring(true);

    const progressToast = toast({
      title: '⭐ Skora myndir...',
      description: `0/${images.length} myndir skoraðar`,
    });

    try {
      const result = await ai.scoreAllPhotos(token, propertyId);

      progressToast.dismiss();

      if (result.success && result.data) {
        setScores(result.data.scores);
        toast({
          title: 'Myndaskorum lokið',
          description: `${result.data.scored}/${result.data.total} myndir skoraðar.${result.data.suggested_hero ? ' Aðalmynd valin.' : ''}`,
          variant: 'success',
        });
      } else {
        toast({
          title: 'Villa',
          description: 'Ekki tókst að skora myndir.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      progressToast.dismiss();
      toast({
        title: 'Villa',
        description: error instanceof Error ? error.message : 'Villa við að skora myndir',
        variant: 'destructive',
      });
    } finally {
      setScoring(false);
    }
  };

  const handleAutoOrder = () => {
    if (!onReorder || !hasScores) return;

    // Sort images by total score descending, unscored last
    const sorted = [...images].sort((a, b) => {
      const scoreA = scores[a]?.total ?? -1;
      const scoreB = scores[b]?.total ?? -1;
      return scoreB - scoreA;
    });

    onReorder(sorted);
    toast({
      title: 'Myndum raðað',
      description: 'Myndir raðaðar eftir AI skori. Aðalmynd er nú hæst skoruð mynd.',
      variant: 'success',
    });
  };

  if (images.length === 0) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
          <Star className="w-4 h-4 text-amber-500" />
          Myndaskori
          {hasScores && (
            <span className="text-xs text-gray-400">
              ({scoredCount}/{images.length} skorað)
            </span>
          )}
        </h4>
        <div className="flex items-center gap-2">
          {hasScores && onReorder && (
            <Button variant="outline" size="sm" onClick={handleAutoOrder}>
              <ArrowUpDown className="w-3 h-3 mr-1" />
              Sjálfvirk röðun
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleScoreAll}
            disabled={scoring || images.length === 0}
          >
            {scoring ? (
              <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Skora...</>
            ) : (
              <><Sparkles className="w-3 h-3 mr-1" />{hasScores ? 'Endurskora' : 'Skora myndir'}</>
            )}
          </Button>
        </div>
      </div>

      {/* Score cards */}
      {hasScores && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {images.map((imageKey, idx) => {
            const score = scores[imageKey];
            if (!score) return null;

            const isHero = idx === 0 || score.suggested_order === 1;
            const filename = imageKey.split('/').pop() || imageKey;

            return (
              <div
                key={imageKey}
                className={cn(
                  'rounded-lg border p-2 text-xs',
                  isHero ? 'border-amber-300 bg-amber-50/50' : 'border-gray-200'
                )}
              >
                {/* Thumbnail */}
                <div className="relative mb-2">
                  <img
                    src={`${API_URL}/api/properties/images/${imageKey}`}
                    alt={filename}
                    className="w-full h-20 object-cover rounded"
                    loading="lazy"
                  />
                  {isHero && (
                    <div className="absolute top-1 left-1 bg-amber-500 text-white text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                      <Trophy className="w-2.5 h-2.5" />
                      Aðalmynd
                    </div>
                  )}
                  <div className={cn(
                    'absolute top-1 right-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full',
                    getScoreColor(score.total)
                  )}>
                    {score.total}
                  </div>
                </div>

                {/* Score details */}
                <p className="font-medium text-gray-900 truncate">
                  {categoryLabels[score.category] || score.category}
                </p>
                <div className="flex items-center justify-between text-gray-500 mt-0.5">
                  <span>Grunnsk: {score.score}</span>
                  <span>+{score.quality} gæði</span>
                </div>
                {score.confidence != null && (
                  <div className="mt-1">
                    <div className="w-full bg-gray-200 rounded-full h-1">
                      <div
                        className="bg-blue-500 rounded-full h-1"
                        style={{ width: `${Math.round(score.confidence * 100)}%` }}
                      />
                    </div>
                    <span className="text-gray-400 text-[10px]">
                      {Math.round(score.confidence * 100)}% viss
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
